import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as ReportIcon } from 'icons/report.svg';
import { formatDateRange, ReportSummary, GroupedReports } from 'utils/reportUtils';

interface ReportsListProps<T extends ReportSummary> {
    groupedReports: GroupedReports<T>;
    onReportClick: (reportId: string) => void;
    onDeleteClick: (reportId: string) => void;
    emptyMessage?: string;
}

function ReportsList<T extends ReportSummary>({
    groupedReports,
    onReportClick,
    onDeleteClick,
    emptyMessage = "No reports available. Create your first report to get started."
}: ReportsListProps<T>) {
    const theme = useTheme();
    const { lastWeekReports, earlierReports } = groupedReports;
    const hasReports = lastWeekReports.length > 0 || earlierReports.length > 0;

    const renderReportItem = (report: T) => (
        <ListItem
            key={report.report_id}
            sx={{ 
                height: 56,
                px: 0,
                borderBottom: '1px solid #e5e7eb'
            }}
        >
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                <ReportIcon 
                    style={{ 
                        width: 24, 
                        height: 24,
                        color: theme.palette.primary.main
                    }} 
                />
            </Box>
            <ListItemText
                primary={formatDateRange(report.start_date, report.end_date)}
                sx={{ flex: 1 }}
            />
            <IconButton
                size="small"
                onClick={() => onDeleteClick(report.report_id)}
                sx={{ 
                    mr: 1,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                        color: theme.palette.error.main
                    }
                }}
            >
                <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Link
                component="button"
                variant="body2"
                onClick={() => onReportClick(report.report_id)}
                sx={{ 
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                        textDecoration: 'underline'
                    }
                }}
            >
                View report
            </Link>
        </ListItem>
    );

    if (!hasReports) {
        return (
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    {emptyMessage}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {/* Last report section */}
            {lastWeekReports.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            mb: 2, 
                            fontWeight: 'bold',
                            color: theme.palette.text.secondary 
                        }}
                    >
                        Last report
                    </Typography>
                    <List>
                        {lastWeekReports.map(renderReportItem)}
                    </List>
                </Box>
            )}

            {/* Earlier section */}
            {earlierReports.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            mb: 2, 
                            fontWeight: 'bold',
                            color: theme.palette.text.secondary 
                        }}
                    >
                        Earlier
                    </Typography>
                    <List>
                        {earlierReports.map(renderReportItem)}
                    </List>
                </Box>
            )}
        </>
    );
}

export default ReportsList;